import React, { useContext, useState } from 'react'
import { Dialog } from '../../components/Dialog'
import { API_HOST } from 'lib/environment'
import { useAutoRefresh, useRemoteData } from 'lib/RemoteData'
import { Link, useNavigate, useParams } from 'react-router'
import { FileStreamDialog } from 'components/FileStreamDialog'
import { createFileStream, deleteFileStream } from 'services/FileStreamService'
import { ConfirmDeletionDialog } from 'components/ConfirmDeletionDialog'
import { SortableHeaderContent, useTableSorting } from 'components/assortment/TableColumnSort'
import { SelectedCountryContext } from 'domain/SelectedCountryContext'
import { SRD } from 'srd'
import { PagedTable } from '@tomra/react-table'
import { DeleteIcon } from '../../icons'
import { hasViewAssortmentPermission } from '../../lib/Permission'
import { LoadingSpinner } from '../../components/LoadingSpinner'

export type CommonAssortmentCountDTO = {
  commonId: string
  name: string
  currentInstallations: number
}

export type FileStreamOverviewDTO = {
  streamId: string
  name: string
  country: string
  parsedVersion?: string
  fileType: 'BARCODE' | 'SHAPE' | 'CRATE' | 'ARTIKELDATEN'
  commonAssortmentCounts: Array<CommonAssortmentCountDTO>
}

export const FileStreamOverview = () => {
  const { filetype } = useParams<{ filetype: string }>()
  const { selectedCountry } = useContext(SelectedCountryContext)
  const { data: fileStreamRemoteData, refresh: refreshFileStream } = useRemoteData<FileStreamOverviewDTO[]>(
    `${API_HOST}/v1/filestream?country=` + selectedCountry,
  )
  useAutoRefresh(refreshFileStream, 60000)
  const navigate = useNavigate()

  const [deletionCandidate, setDeleteCandidate] = useState<[string, string] | undefined>()
  const [showCreateFileStreamDialog, setShowCreateFileStreamDialog] = useState(false)

  const availableTypes = ['Shape', 'Barcode', 'Crate', selectedCountry === 'DE' ? 'Artikeldaten' : ''].filter(
    (t) => t !== '',
  )

  const getTabFromParam = (param?: string) => {
    if (!param) return null
    return availableTypes.find((t) => t?.toLowerCase() === param.toLowerCase())
  }

  const activeTab = getTabFromParam(filetype) || availableTypes[0]

  const sorting = useTableSorting<FileStreamOverviewDTO>(
    {
      Name: (fs) => fs.name,
      '# Common Assortments': (fs) => {
        const count: string = fs.commonAssortmentCounts.length.toString()
        return ('00000' + count).substring(count.length)
      },
      '# Installations': (fs) => {
        const count: string = fs.commonAssortmentCounts
          .reduce((acc, ref) => acc + ref.currentInstallations, 0)
          .toString()
        return ('00000' + count).substring(count.length)
      },
    },
    'Name',
  )

  return (
    hasViewAssortmentPermission() &&
    SRD.match(
      {
        notAsked: () => <LoadingSpinner />,
        loading: () => <LoadingSpinner />,
        failure: (err) => (
          <div className="card">
            <div className="centerAbsolute">
              <div className="alert alert-danger">
                Uh-oh.. Failed to load File Stream Overview
                <div>{err.body?.title}</div>
              </div>
            </div>
          </div>
        ),
        success: (value) => {
          const availableInCountry = value.filter((fileStream) => fileStream.country === selectedCountry)
          const dataSelection = availableInCountry.filter(
            (fileStream) => fileStream.fileType === activeTab?.toUpperCase(),
          )

          return (
            <div className="card">
              <div className="p-md">
                <button className="btn btn-primary-dark py-sm" onClick={() => setShowCreateFileStreamDialog(true)}>
                  Create File Stream
                </button>
              </div>
              <div>
                <div className="tabs w-6/12 mb-1">
                  {availableTypes.map((type) => {
                    return (
                      <button
                        key={type}
                        className={`tab ${activeTab === type ? 'active' : ''}`}
                        onClick={() => {
                          navigate(`/file-stream/file-type/${type?.toLowerCase()}`)
                        }}
                      >
                        {type}
                      </button>
                    )
                  })}
                </div>
                <PagedTable
                  data={sorting.sort(dataSelection)}
                  columnHeaders={[
                    <div style={{ minWidth: '14em' }} key="name-header">
                      <SortableHeaderContent name="Name" currentSort={sorting.currentSort} />
                    </div>,
                    <div style={{ minWidth: '14em' }} key="common-assortments-header">
                      <SortableHeaderContent name="# Common Assortments" currentSort={sorting.currentSort} />
                    </div>,
                    <div style={{ minWidth: '14em' }} key="installations-header">
                      <SortableHeaderContent name="# Installations" currentSort={sorting.currentSort} />
                    </div>,
                    'Delete',
                  ]}
                  rowFactory={(fileStream) => {
                    const link = `/file-stream/${fileStream.streamId}`
                    return (
                      <tr key={fileStream.streamId} className="even:bg-grey-light hover:bg-fog">
                        <td>
                          <Link to={link} className="link">
                            {fileStream.name}
                          </Link>
                        </td>
                        <td>{fileStream.commonAssortmentCounts.length}</td>
                        <td>
                          {fileStream.commonAssortmentCounts.reduce((acc, ref) => acc + ref.currentInstallations, 0)}
                        </td>
                        <td>
                          <button
                            className="btn btn-icon"
                            title="Delete"
                            onClick={() => setDeleteCandidate([fileStream.name, fileStream.streamId])}
                          >
                            <DeleteIcon />
                          </button>
                        </td>
                      </tr>
                    )
                  }}
                  initialRecordsPerPage={100}
                  className="table"
                />
              </div>
              {showCreateFileStreamDialog ? (
                <Dialog onClose={() => setShowCreateFileStreamDialog(false)} shouldCloseOnOverlayClick={false}>
                  <FileStreamDialog
                    onComplete={(name, type) =>
                      createFileStream(name, type, selectedCountry).then((_) => {
                        setShowCreateFileStreamDialog(false)
                        refreshFileStream()
                      })
                    }
                  />
                </Dialog>
              ) : null}
              {deletionCandidate ? (
                <Dialog onClose={() => setDeleteCandidate(undefined)} shouldCloseOnOverlayClick={false}>
                  <ConfirmDeletionDialog
                    entityName={deletionCandidate[0]}
                    onConfirm={async () => {
                      await deleteFileStream(deletionCandidate[1])
                      setDeleteCandidate(undefined)
                      refreshFileStream()
                    }}
                    onClose={async () => setDeleteCandidate(undefined)}
                  />
                </Dialog>
              ) : null}
            </div>
          )
        },
      },
      fileStreamRemoteData,
    )
  )
}
